import { inject, observer } from "mobx-react";
import React from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { ConfirmationDialog, Heading, Icon, Modal, Paper, Text } from "src/components";
import { Badge } from "src/components/badge";
import { theme } from "src/config";
import { Space, Tag, User } from "src/models";
import { getRoutePath, MatchParams, Page, RoomView } from "src/routes";
import { RoutingStore, SpaceStore, TagStore, UserStore, AuthStore } from "src/stores";
import { SpaceType, UserRole, UserType, UserStatus } from "src/types";
import { sortAlph, without } from "src/util/util";
import styled from "styled-components";
import { GroupCard, RoomCard, TagCard, UserCard } from "./cards";
import { Section } from "./section";


export interface IProjectProps{
  authStore?:    AuthStore,
  userStore?:    UserStore,
  spaceStore?:   SpaceStore,
  tagStore?:     TagStore,
  routingStore?: RoutingStore,
  params?:       MatchParams;
}

const Container = styled.div`
  padding: 0vw 2rem;
`;

export const ProjectSettings = inject("authStore", "userStore", "tagStore", "spaceStore", "routingStore")(observer((props: IProjectProps): JSX.Element => {
  const { authStore, userStore, spaceStore, routingStore, tagStore } = props;
  const { t, i18n } = useTranslation();

  const [showConfirmDialog, setShowConfirmDialog] = React.useState<boolean>(false);
  const toLink = React.useRef<number[]>([]);

  const projRooms:  Space[] = spaceStore.getItemsByParent(authStore.getCurProjectID, SpaceType.Room);
  const users:      User[]  = userStore.getUsersBySpace(authStore.getCurWorkspaceID, [UserType.Default, UserType.Accountless]);
  const projUsers:  User[]  = userStore.getUsersBySpace(authStore.getCurProjectID,   [UserType.Default, UserType.Accountless]);
  const projTags:   Tag[]   = tagStore.getProjectTags;
  const tags:       Tag[]   = tagStore.getTagsNotInSpace(authStore.getCurProjectID);
  // const projGroups: User[]  = userStore.getUsersBySpace(authStore.getCurProjectID,   [UserType.Group]);
  // const groups:     User[]  = userStore.getUsersBySpace(authStore.getCurWorkspaceID, [UserType.Group]);

  const project = spaceStore.getItemByID(authStore.getCurProjectID);
  const closeCards = React.useCallback(() => {
    spaceStore.setActive(null);
    tagStore.setActive(null);
    userStore.setActive(null);
  }, []);

  React.useEffect(() => {
    if(Number(props.params.id) !== authStore.getCurProjectID) {
      spaceStore.changeProject(Number(props.params.id));
    }
    closeCards();
  }, []);

  
  const currentUser = authStore.user;
  if( !currentUser.canEdit) return null;
  
  return (
    <Container>
      <Heading type="h3">{project && project.title}</Heading>
      {/* Member Section */}
      <Section
        currentUser = {currentUser}
        data       = {[]}
        addData    = {[]}
        dataAlt    = {projUsers}
        addDataAlt = {without(users, projUsers)}
        space_id   = {authStore.getCurProjectID}
        title      = {t("project_users")}
        newToolTip = {t("tooltip_new_user")}
        newIcon    = {<Badge border="2px" height="1.8rem" width="1.8rem" content={<Icon size="xxsmall" color={theme.color.white} iconId="add"/>}><Icon iconId="person" size="xxlarge" color={theme.color.grayDark}/></Badge>}
        newToolTipAlt = {t("tooltip_new_project_group")}
        newIconAlt = {<Badge border="2px" height="1.8rem" width="1.8rem" content={<Icon size="xxsmall" color={theme.color.white} iconId="add"/>}><Icon iconId="group" size="xxlarge" color={theme.color.grayDark}/></Badge>}
        Card       = {GroupCard}
        CardAlt    = {UserCard}
        // onNewAlt   = {async () => {
        //   try {
        //     await userStore.createGroup(`${t("new_group")}`, authStore.getCurProjectID);
        //     toast.success(t('new_group_created'))
        //   } catch ( error ) {
        //     console.error("Failed to add new group to project", error)
        //     toast.error(t('failed_to_create_group'))
        //   }
        // }}
        onNew = {() => {
          routingStore.push(getRoutePath(Page.UserInvite, {view: "project", id: authStore.getCurProjectID}))
        }}
        onAdd   = {async (group: User) => {
          try {
            await userStore.createGroup(group.first_name, authStore.getCurProjectID);
            toast.success(t('new_group_created'))
          } catch (err) {
            toast.error(t('failed_added_group_project'))
          }
        }}
        onRemove = {async (group: User) => { 
          try {
            await group.unlinkSpace(authStore.getCurProjectID);
            toast.success(t('removed_group_project'));
          } catch (err) {
            toast.error(t('failed_removed_group_project'))
          }
        }}
        onAddAlt    = {async (user: User) => {
          try {
            const status = user.getStatus(spaceStore.getCurSpace.id);
            let role     = user.spaces[authStore.getCurWorkspaceID].role;
            if( role == UserRole.Owner ) role = UserRole.Leader;
            await user.linkSpace(authStore.getCurProjectID, SpaceType.Project, role, status);
            toast.success(t('added_user_project'));
          } catch (err) {
            toast.error(t('failed_added_user_project'))
          }
        }}
        onRemoveAlt = {async (user: User) => {
          try {
            await user.unlinkSpace(authStore.getCurProjectID);
            toast.success(t('removed_user_project'));
          } catch (err) {
            toast.error(t('failed_removed_user_project'))
          }
        }}
      />
      <Modal show={showConfirmDialog}>
        <ConfirmationDialog
          confirmButtonLabel = {t('confirm')}
          onCancel  = {() => {setShowConfirmDialog(false); toLink.current = [];}}
          label     = {t("confirm_action")}
          message   = {t("confirm_action_add_additional_users_message_project")}
          content   = {
            <ul>
              {toLink.current.map(id => (
                <li key={id}>
                  <Text size="small" color={theme.color.slate12}> 
                  {userStore.getItemByID(id)?.getFullName}
                  </Text>
                  </li>
              ))}
            </ul>
          }
          onConfirm = {async () => {
            for(const id of toLink.current) {
              // Link the group and all missing users to that project
              const user = userStore.getItemByID(id);
              let role     = user.spaces[authStore.getCurWorkspaceID].role;
              if( role == UserRole.Owner ) role = UserRole.Leader;
              await user.linkSpace(authStore.getCurProjectID, SpaceType.Project, role, UserStatus.Accepted);
              user.type === UserType.Group ?
                toast.success(t('added_group_project', {name: user.getFullName})) :
                toast.success(t('added_user_project',  {name: user.getFullName}));
            }
            toLink.current = [];
            setShowConfirmDialog(false);
          }}
        />
      </Modal>

      {/* Room Section */}

      <Section
        currentUser = {currentUser}
        data       = {projRooms}
        title      = {t("project_rooms")}
        newToolTip = {t("tooltip_new_project_room")}
        newIcon    = {<Badge border="2px" height="1.8rem" width="1.8rem" content={<Icon size="xxsmall" color={theme.color.white} iconId="add"/>}><Icon size="xxlarge" iconId="room" color={theme.color.grayDark}/></Badge>}
        Card       = {RoomCard}
        onNew      = {async () => {
          const space = Space.create({
            title:        `${t("meetingroom")}`,
            type:         SpaceType.Room,
            parent:       authStore.getCurProjectID,
            workspace_id: authStore.getCurWorkspaceID,
            project_id:   authStore.getCurProjectID,
          });
          try {
            await space.save();
            spaceStore.setActive(space);
            toast.success(t('created_room'));
          } catch (err) {
            toast.error(t('failed_created_room'))
          }
        }}
      />

      {/* Tag Section */}

      <Section
        currentUser = {currentUser}
        data       = {projTags}
        addData    = {tags}
        title      = {t("tag_plural")}
        newToolTip    = {t("tooltip_new_tag")}
        newIcon    = {<Badge border="2px" height="1.8rem" width="1.8rem" content={<Icon size="xxsmall" color={theme.color.white} iconId="add"/>}><Icon iconId="tag" size="xxlarge" color={theme.color.grayDark}/></Badge>}
        Card       = {TagCard}
        onNew      = {async () => {
          const tag = Tag.create({
            title:         `${t("new_tag")}`,
            workspace_id: authStore.getCurWorkspaceID,
          });
          try {
            await tag.save();
            await tag.linkSpace(authStore.getCurProjectID);
            toast.success(`${t('new_tag_created')}: ${tag.title}`)
          } catch(error) {
            toast.error(`${t('failed_to_create_tag')}`)
          }
        }}
        onAdd = {async (tag: Tag) => {
          try {
            await tag.linkSpace(authStore.getCurProjectID);
            toast.success(`${t('add_tag')}: ${tag.title}`)
          } catch(error) {
            toast.error(`${t('failed_add_tag')}`)
          }
        }}
        onRemove = {async (tag: Tag) => { 
          try {
            await tag.unlinkSpace(authStore.getCurProjectID);
            toast.success(`${t('remove_tag')}: ${tag.title}`)
          } catch(error) {
            toast.error(`${t('failed_remove_tag')}`)
          }
        }}
      />
    </Container>
  );
}));